/*
 * anchor-nav — Level 2 (CSP build) sticky section-nav with IntersectionObserver
 * scroll-spy.
 *
 * LIBRARY TIER (inert): this component is NOT wired into kit chrome and its CSS
 * (resources/css/components/_anchor-nav.css) is imported only by docs.css, never
 * production app.css — mirroring the inert block-library tier. Opt in per project
 * by rendering the partial, importing that CSS into app.css, and giving each
 * target section a matching id.
 *
 * Without JS (or without IntersectionObserver) the nav is just a list of in-page
 * anchor links that jump to each section — fully usable. When JS is present, the
 * link whose section is currently in view gets `.is-active` + aria-current, both
 * toggled imperatively so there are NO per-link Alpine expressions to trip the
 * CSP evaluator (name-only x-data only).
 *
 * THE ACTIVE SECTION IS RESOLVED, NOT LATCHED. `rootMargin` shrinks the root to
 * the top 30% of the viewport, and a section taller than that band overlaps it at
 * the same time as its neighbour — so at any moment SEVERAL sections can be
 * intersecting. Acting on each entry as it arrives ("if isIntersecting, activate")
 * therefore lets the firing ORDER decide the answer, and firing order depends on
 * which way you are scrolling: coming down, the newly-entering section is the one
 * below and the highlight is right; going up, the newly-entering section is the
 * one ABOVE, so it stole the highlight while the section still filling the screen
 * was merely unchanged and never re-fired. Scrolling up past 'Approach' jumped
 * straight to 'Overview'.
 *
 * Instead every entry is stored and the winner is recomputed from the whole set:
 * the LAST intersecting section in document order — the one furthest down that has
 * reached the band. That yields ONE threshold per boundary, crossed identically in
 * both directions, so the sequence up is the exact reverse of the sequence down.
 * Only `isIntersecting` is read, off the entry the observer already provides, so
 * no layout is forced during scroll.
 *
 * Registered in resources/js/app.js as: Alpine.data('anchorNav', anchorNav)
 * Paired with resources/views/components/interactive/anchor-nav.antlers.html.
 */
export default function anchorNav() {
    return {
        _io: null,
        _links: {},
        /** Observed sections, in DOCUMENT order — the order the winner is picked in. */
        _sections: [],
        /** id → latest IntersectionObserverEntry. */
        _entries: null,
        /** Currently highlighted id, so an unchanged winner does no DOM work. */
        _activeId: null,

        init() {
            const links = Array.from(this.$root.querySelectorAll('[data-anchor-nav-link]'));
            if (links.length === 0) {
                return;
            }

            const sections = [];
            this._links = {};

            links.forEach((link) => {
                const id = link.getAttribute('data-anchor-nav-link');
                this._links[id] = link;
                const section = document.getElementById(id);
                if (section) {
                    sections.push(section);
                }
            });

            // No IO or no resolvable sections: leave the plain anchors as the
            // fallback (native href jumps still work). Nothing to enhance.
            if (!('IntersectionObserver' in window) || sections.length === 0) {
                return;
            }

            this._sections = sections;
            this._entries = new Map();

            this._io = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => this._entries.set(entry.target.id, entry));
                    this._resolveActive();
                },
                { rootMargin: '0px 0px -70% 0px', threshold: 0 },
            );

            sections.forEach((section) => this._io.observe(section));
        },

        /**
         * Pick the active section from the CURRENT state of every observed section
         * rather than from the entry that happened to arrive last: the last one in
         * document order that is intersecting the band.
         *
         * With nothing intersecting — above the first section, or below the last if
         * it is shorter than the band — the previous highlight is KEPT. Clearing it
         * would blank the nav at the top and bottom of the page, which reads as the
         * spy having failed rather than as "no section is under the cursor".
         */
        _resolveActive() {
            let winner = null;

            for (const section of this._sections) {
                const entry = this._entries.get(section.id);
                if (entry && entry.isIntersecting) {
                    winner = section.id;
                }
            }

            if (winner === null || winner === this._activeId) {
                return;
            }

            this._activeId = winner;
            this.setActive(winner);
        },

        setActive(id) {
            Object.keys(this._links).forEach((key) => {
                const link = this._links[key];
                const isActive = key === id;
                link.classList.toggle('is-active', isActive);
                if (isActive) {
                    link.setAttribute('aria-current', 'true');
                } else {
                    link.removeAttribute('aria-current');
                }
            });
        },

        destroy() {
            if (this._io) {
                this._io.disconnect();
                this._io = null;
            }
            this._links = {};
        },
    };
}
